import React, { useState } from 'react';
import PropTypes from 'prop-types';
import HelpModal from 'components/NewDesign/HelpModal';
import SeasonPointsBadge from 'components/admin/SeasonPointsBadge';
import PointsExplanationList from 'components/admin/PointsExplanationList';
import useOrganisationWords from 'hooks/useOrganisationWords';

// Clickable Season points badge + explainer dialog for the Meet Details page.
// Reports the points on offer at this meet in the server's own words (the
// meet's explanation lines: its own points, or the club's), or explains that
// the meet is excluded from season points. Only ways that earn points are
// listed; the Meet Points page's help shows the same lines.
//
// For a school (an organisation with houses) the server's lines open with how its house points are
// worked out, and the season sentence follows the lists, worded for a school (Doug, 2026-10-24, H8).
const SeasonPointsHelp = ({
  countsForSeasonPoints,
  countsForSeasonBests,
  pointsExplanation = [],
  hasHouses = false,
}) => {
  const [showHelpModal, setShowHelpModal] = useState(false);

  const awarded = countsForSeasonPoints !== false;
  // Season bests are a separate meet setting (SB-6); they normally match the points setting.
  const bestsCount = countsForSeasonBests !== false;

  const hasAnyPoints = (pointsExplanation || []).length > 0;
  // "club records", or "school records" for a school (Doug, 2026-10-27). Meet Details' address names the club.
  const { word: organisationWord } = useOrganisationWords();

  const seasonBestsNote = bestsCount ? (
    <>
      <h3>Season Bests</h3>
      <p>
        Results at this meet count for season bests and can become{' '}
        {organisationWord} records. An athlete&apos;s first result in an event this season sets
        their season best, and a later result that beats it earns a star.
      </p>
    </>
  ) : (
    <>
      <h3>Season Bests</h3>
      <p>
        Results at this meet do not count for season bests or {organisationWord} records:
        they are not starred and do not set or beat an athlete&apos;s season
        best. They still count for all-time personal bests.
      </p>
    </>
  );

  return (
    <>
      <SeasonPointsBadge
        countsForSeasonPoints={countsForSeasonPoints}
        countsForSeasonBests={countsForSeasonBests}
        onClick={() => setShowHelpModal(true)}
      />

      <HelpModal
        open={showHelpModal}
        title="Points Awarded at This Meet"
        onClose={() => setShowHelpModal(false)}
      >
        {awarded ? (
          <>
            {!hasHouses && (
              <p>
                Athletes earn season points at this meet. Points are added to
                each athlete&apos;s total in the club season points standings.
              </p>
            )}

            <PointsExplanationList sections={pointsExplanation || []} />

            {hasHouses && <p>Each athlete&apos;s points also add to their own season total.</p>}

            {!hasAnyPoints && (
              <p>No points values have been configured for this meet.</p>
            )}

            {seasonBestsNote}
          </>
        ) : (
          <>
            <h3>This Meet Does Not Count for Season Points</h3>
            <p>
              Season points are not awarded at this meet. Results are still
              recorded and appear in the meet results, but they do not add to
              athletes&apos; season points totals.
            </p>

            {seasonBestsNote}
          </>
        )}
      </HelpModal>
    </>
  );
};

SeasonPointsHelp.propTypes = {
  countsForSeasonPoints: PropTypes.bool,
  countsForSeasonBests: PropTypes.bool,
  // The meet's explanation lines (MeetDto.pointsExplanation): PointsExplanationList's sections.
  pointsExplanation: PropTypes.arrayOf(PropTypes.object),
  // The organisation has houses (clubFeaturesOf(club).teams): a school.
  hasHouses: PropTypes.bool,
};

export default SeasonPointsHelp;
